import { useState } from 'react'
import { useDealColumn } from '../../api/queries/deals'
import type { DealFilters, ColumnFilter } from '../../api/queries/deals'
import { userFacingMessage } from '../../api/errors'
import { Skeleton } from '../../ui/Feedback'
import type { Grouping } from '../../lib/boardView'
import { DealCard } from './DealCard'
import { DECISION_COLUMNS, STAGE_COLUMNS } from './grouping'
import styles from './Board.module.css'

/**
 * Two presentations of the same authoritative data. Column membership is a
 * comparison against a backend field — never a rule of our own.
 *
 * Each column fetches its own page. The number in the header is the column's
 * total from the server, not the number of cards below it: an account with a
 * hundred deals in one stage shows twenty and says so, and the header keeps
 * telling the truth about the other eighty.
 */

/** How many cards a column shows before it offers the rest. */
const COLUMN_PAGE = 20

function Column({
  filters,
  column,
  label,
  accent,
}: {
  filters: DealFilters
  column: ColumnFilter
  label: string
  accent?: string
}) {
  const [limit, setLimit] = useState(COLUMN_PAGE)
  const page = useDealColumn(filters, column, limit)
  const deals = page.data?.deals ?? []
  const total = page.data?.total ?? 0
  const hidden = Math.max(total - deals.length, 0)

  return (
    <section
      className={`${styles.column} ${!page.isPending && total === 0 ? styles.columnEmpty : ''}`}
      aria-label={`${label}, ${total} deals`}
    >
      <header className={styles.head}>
        <h2 className={styles.title} style={accent ? { color: accent } : undefined}>
          {label}
        </h2>
        <span className={`${styles.count} ${total === 0 ? styles.countZero : ''}`}>
          {page.isPending ? '' : total}
        </span>
      </header>
      {page.isPending ? (
        <Skeleton height={120} />
      ) : page.isError ? (
        <p className={styles.empty}>{userFacingMessage(page.error)}</p>
      ) : deals.length === 0 ? (
        <p className={styles.empty}>No deals</p>
      ) : (
        <>
          {deals.map((deal) => (
            <DealCard key={deal.id} deal={deal} />
          ))}
          {hidden > 0 ? (
            <button type="button" className={styles.more} onClick={() => setLimit((current) => current + COLUMN_PAGE)}>
              Show {hidden} more
            </button>
          ) : null}
        </>
      )}
    </section>
  )
}

export function Board({ filters, grouping }: { filters: DealFilters; grouping: Grouping }) {
  // The filters are part of the key so a new search starts every column at its
  // first page again, rather than leaving one column expanded to eighty cards
  // of a set the user has just replaced.
  const reset = `${filters.search}|${filters.priority}|${filters.archived}`

  return (
    <div className={grouping === 'stage' ? styles.boardWide : styles.board}>
      {grouping === 'stage'
        ? STAGE_COLUMNS.map((column) => (
            <Column key={`${column.stage}:${reset}`} filters={filters} column={{ stage: column.stage }} label={column.label} />
          ))
        : DECISION_COLUMNS.map((column) => (
            <Column
              key={`${column.decision ?? 'none'}:${reset}`}
              filters={filters}
              column={{ decision: column.decision ?? 'none' }}
              label={column.label}
              accent={column.accent}
            />
          ))}
    </div>
  )
}
